import { useEffect, useRef, type ReactNode } from 'react';
import { de } from '../i18n/de';

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}

export function Modal({ title, onClose, children, footer, wide }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // `onClose` ist meist eine neue Funktion pro Render. Der Effekt darf deshalb nicht davon abhängen, sonst würde der Fokus
  // nach jedem Tastendruck (der neu rendert) wieder auf den Dialog springen und das Eingabefeld verlassen.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    // nur beim Öffnen und nur, wenn nicht schon ein Feld im Dialog den Fokus hat (autoFocus)
    if (ref.current && !ref.current.contains(document.activeElement)) ref.current.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn ghost icon" onClick={onClose} aria-label={de.common.close}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
